import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  LOCALE_ID,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DatePipe, LowerCasePipe, NgClass, NgTemplateOutlet} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {MatIcon} from '@angular/material/icon';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';

import {TogetherService, TOGETHER_PDF_MAX_BYTES, TOGETHER_IMAGE_MAX_BYTES} from './together.service';
import {
  TogetherBild,
  TogetherFilter,
  TogetherKategorie,
  TogetherPost,
  TogetherAnhangArt,
  TogetherPostCreateRequest,
} from './together.model';
import {UserRoleService} from '../../services/userRole.service';
import {ModalService} from '../../shared/components/modal/modal.service';
import {formatFileSize} from '../../shared/attachment-download';
import {FileDropzoneComponent} from './file-dropzone/file-dropzone.component';
import {TogetherLightboxComponent} from './together-lightbox.component';
import {ModalComponent} from '../../shared/components/modal/modal.component';

type StateText = 'required' | 'attachmentFile' | 'attachmentUrl' | 'ready';

const DELETE_MODAL = 'together-delete';
const COMPOSER_MODAL = 'together-composer';

@Component({
  selector: 'app-together',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NgTemplateOutlet,
    LowerCasePipe,
    FormsModule,
    MatIcon,
    MatProgressSpinner,
    TranslatePipe,
    FileDropzoneComponent,
    TogetherLightboxComponent,
    ModalComponent,
  ],
  providers: [DatePipe],
  templateUrl: './together.component.html',
  styleUrl: './together.component.scss',
})
export class TogetherComponent implements OnInit, OnDestroy {
  private readonly service = inject(TogetherService);
  private readonly userRole = inject(UserRoleService);
  private readonly modal = inject(ModalService);
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly datePipe = inject(DatePipe);
  private readonly locale = inject(LOCALE_ID);

  readonly pdfMax = TOGETHER_PDF_MAX_BYTES;
  readonly imageMax = TOGETHER_IMAGE_MAX_BYTES;

  readonly isOfficeOrAdmin = this.userRole.includesRoles(['Office', 'Admin']);

  readonly composerModalId = COMPOSER_MODAL;

  posts: TogetherPost[] = [];
  loading = true;
  loadError = false;

  activeFilter: TogetherFilter = 'ALLE';

  editingPost: TogetherPost | null = null;
  composerTrigger: HTMLElement | null = null;

  composerTitle = '';
  composerDate = this.todayIso();
  composerKategorie: TogetherKategorie = 'MEILENSTEIN';
  composerText = '';
  composerAnhangArt: TogetherAnhangArt = 'KEIN_ANHANG';
  composerAnzeigeName = '';
  composerUrl = '';
  composerUrlError: string | null = null;
  composerPdf: File | null = null;
  composerPdfError: string | null = null;
  composerPdfExistsOnServer = false;
  composerImage: File | null = null;
  composerImagePreview: string | null = null;
  composerImageError: string | null = null;
  composerImageDescription = '';
  composerExistingBild: TogetherBild | null = null;
  publishing = false;
  publishError: string | null = null;

  postToDelete: TogetherPost | null = null;
  deleteModalId = DELETE_MODAL;

  lightboxOpen = false;
  lightboxUrl: string | null = null;
  lightboxTitel = '';
  lightboxBeschreibung: string | null = null;
  lightboxTrigger: HTMLElement | null = null;

  downloadError: string | null = null;
  deleteError: string | null = null;
  composerDateError: string | null = null;

  imageUrls = new Map<string, string>();

  get filteredPosts(): TogetherPost[] {
    if (this.activeFilter === 'ALLE') return this.posts;
    return this.posts.filter(p => p.kategorie === this.activeFilter);
  }

  get startYear(): number | null {
    const first = this.filteredPosts[0];
    return first ? new Date(first.datum).getFullYear() : null;
  }

  get stateText(): StateText {
    const titleOk = this.composerTitle.trim().length >= 3;
    const dateOk = this.composerDate.trim().length > 0;
    const textOk = this.composerText.trim().length >= 3;
    if (!titleOk || !dateOk || !textOk) return 'required';
    if (this.composerAnhangArt === 'PDF' && (!this.composerAnzeigeName.trim() || (!this.composerPdf && !this.composerPdfExistsOnServer))) {
      return 'attachmentFile';
    }
    if (this.composerAnhangArt === 'LINK' && (!this.composerAnzeigeName.trim() || !this.composerUrl.trim())) {
      return 'attachmentUrl';
    }
    return 'ready';
  }

  get canPublish(): boolean {
    if (this.composerDateError) return false;
    if (this.composerAnhangArt === 'LINK' && this.composerUrl.trim()) {
      const url = this.composerUrl.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        return false;
      }
    }
    return this.stateText === 'ready' && !this.publishing;
  }

  ngOnInit(): void {
    this.loadPosts();
  }

  loadPosts(): void {
    this.loading = true;
    this.loadError = false;
    this.cdr.markForCheck();
    this.service
      .loadPosts()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: posts => {
          this.posts = posts;
          this.loading = false;
          this.loadImageUrls(posts);
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.loadError = true;
          this.cdr.markForCheck();
        },
      });
  }

  private loadImageUrls(posts: TogetherPost[]): void {
    for (const post of posts) {
      if (post.bild && !this.imageUrls.has(post.id)) {
        this.service
          .loadImageBlob(post.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: blob => {
              const url = URL.createObjectURL(blob);
              this.imageUrls.set(post.id, url);
              this.cdr.markForCheck();
            },
          });
      }
    }
  }

  setFilter(f: TogetherFilter): void {
    this.activeFilter = f;
  }

  openNewComposer(event: MouseEvent): void {
    this.composerTrigger = event.currentTarget as HTMLElement;
    this.editingPost = null;
    this.resetComposer();
    this.modal.open(COMPOSER_MODAL);
  }

  openEditComposer(post: TogetherPost, event: MouseEvent): void {
    this.composerTrigger = event.currentTarget as HTMLElement;
    this.editingPost = post;
    this.composerTitle = post.titel;
    this.composerDate = post.datum;
    this.composerKategorie = post.kategorie;
    this.composerText = post.text;
    if (post.anhang?.art === 'LINK') {
      this.composerAnhangArt = 'LINK';
      this.composerAnzeigeName = post.anhang.name ?? '';
      this.composerUrl = post.anhang.ziel ?? '';
    } else if (post.anhang?.art === 'PDF') {
      this.composerAnhangArt = 'PDF';
      this.composerAnzeigeName = post.anhang.name ?? '';
      this.composerPdfExistsOnServer = true;
    } else {
      this.composerAnhangArt = 'KEIN_ANHANG';
      this.composerAnzeigeName = '';
      this.composerPdfExistsOnServer = false;
    }
    this.composerPdf = null;
    this.composerPdfError = null;
    this.composerImage = null;
    if (this.composerImagePreview) {
      URL.revokeObjectURL(this.composerImagePreview);
      this.composerImagePreview = null;
    }
    this.composerImageError = null;
    this.composerExistingBild = post.bild ?? null;
    this.composerImageDescription = post.bild?.beschreibung ?? '';
    this.composerUrlError = null;
    this.publishError = null;
    this.cdr.markForCheck();
    this.modal.open(COMPOSER_MODAL);
  }

  onAnhangArtChange(art: TogetherAnhangArt): void {
    this.composerAnhangArt = art;
    this.composerPdf = null;
    this.composerPdfError = null;
    this.composerPdfExistsOnServer = false;
    this.composerUrlError = null;
  }

  onPdfSelected(file: File): void {
    this.composerPdf = file;
    this.composerPdfError = null;
    if (!this.composerAnzeigeName.trim()) {
      this.composerAnzeigeName = file.name.replace(/\.[^.]+$/, '');
    }
  }

  onPdfRejected(key: string): void {
    this.composerPdfError = key;
  }

  removePdf(): void {
    this.composerPdf = null;
    this.composerPdfError = null;
  }

  removeExistingPdf(): void {
    this.composerPdfExistsOnServer = false;
    this.composerPdf = null;
    this.composerPdfError = null;
  }

  removeExistingImage(): void {
    this.composerExistingBild = null;
    this.composerImageDescription = '';
  }

  onImageSelected(file: File): void {
    if (this.composerImagePreview) {
      URL.revokeObjectURL(this.composerImagePreview);
    }
    this.composerImage = file;
    this.composerImagePreview = URL.createObjectURL(file);
    this.composerImageError = null;
  }

  onImageRejected(key: string): void {
    this.composerImageError = key;
    this.composerImage = null;
    if (this.composerImagePreview) {
      URL.revokeObjectURL(this.composerImagePreview);
      this.composerImagePreview = null;
    }
  }

  onReplaceImage(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      this.onImageSelected(file);
    }
    (event.target as HTMLInputElement).value = '';
  }

  removeImage(): void {
    this.composerImage = null;
    if (this.composerImagePreview) {
      URL.revokeObjectURL(this.composerImagePreview);
      this.composerImagePreview = null;
    }
    this.composerImageError = null;
    this.composerImageDescription = '';
  }

  validateDate(): void {
    if (!this.composerDate) {
      this.composerDateError = null;
      return;
    }
    const year = new Date(this.composerDate).getFullYear();
    this.composerDateError = isNaN(year) || year < 2000 || year > 2099
      ? 'together.validation.dateRange'
      : null;
  }

  validateUrl(): void {
    if (this.composerAnhangArt === 'LINK' && this.composerUrl.trim()) {
      const url = this.composerUrl.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        this.composerUrlError = 'together.validation.urlInvalid';
      } else {
        this.composerUrlError = null;
      }
    } else {
      this.composerUrlError = null;
    }
  }

  pdfFileSize(): string {
    return this.composerPdf ? formatFileSize(this.composerPdf.size, this.translate.currentLang) : '';
  }

  publish(): void {
    if (!this.canPublish || this.publishing) return;
    this.publishing = true;
    this.publishError = null;
    this.cdr.markForCheck();

    const hasNewImage = !!this.composerImage;
    const keepingImage = hasNewImage || !!this.composerExistingBild;
    const bildBeschreibung = keepingImage
      ? (this.composerImageDescription.trim() || '')
      : null;
    const bildEntfernen =
      !!this.editingPost && !!this.editingPost.bild && !this.composerExistingBild && !hasNewImage
        ? true
        : undefined;

    const req: TogetherPostCreateRequest = {
      titel: this.composerTitle.trim(),
      datum: this.composerDate,
      kategorie: this.composerKategorie,
      text: this.composerText.trim(),
      anhangArt: this.composerAnhangArt,
      anzeigeName: this.composerAnhangArt !== 'KEIN_ANHANG' ? this.composerAnzeigeName.trim() : null,
      url: this.composerAnhangArt === 'LINK' ? this.composerUrl.trim() : null,
      bildBeschreibung,
      bildEntfernen,
    };

    const pdf = this.composerAnhangArt === 'PDF' ? this.composerPdf : null;

    if (this.editingPost) {
      const editId = this.editingPost.id;
      this.service
        .updatePost(editId, req, pdf, this.composerImage)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: updatedPost => {
            const oldBlobUrl = this.imageUrls.get(editId);
            if (oldBlobUrl) {
              URL.revokeObjectURL(oldBlobUrl);
              this.imageUrls.delete(editId);
            }
            this.posts = this.posts
              .map(p => (p.id === editId ? updatedPost : p))
              .sort((a, b) => a.datum.localeCompare(b.datum));
            if (updatedPost.bild) {
              this.loadImageUrls([updatedPost]);
            }
            this.editingPost = null;
            this.resetComposer();
            this.publishing = false;
            this.modal.close(COMPOSER_MODAL);
            this.cdr.markForCheck();
          },
          error: err => {
            this.publishing = false;
            if (err?.status === 404) {
              this.publishError = 'together.error.postNotFound';
              this.loadPosts();
            } else {
              this.publishError = this.mapPublishError(err);
            }
            this.cdr.markForCheck();
          },
        });
    } else {
      this.service
        .createPost(req, pdf, this.composerImage)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: newPost => {
            this.posts = [...this.posts, newPost].sort((a, b) => a.datum.localeCompare(b.datum));
            if (newPost.bild) {
              this.loadImageUrls([newPost]);
            }
            this.resetComposer();
            this.publishing = false;
            this.modal.close(COMPOSER_MODAL);
            this.cdr.markForCheck();
          },
          error: err => {
            this.publishing = false;
            this.publishError = this.mapPublishError(err);
            this.cdr.markForCheck();
          },
        });
    }
  }

  discard(): void {
    this.editingPost = null;
    this.resetComposer();
    this.modal.close(COMPOSER_MODAL);
  }

  private resetComposer(): void {
    this.composerTitle = '';
    this.composerDate = this.todayIso();
    this.composerKategorie = 'MEILENSTEIN';
    this.composerText = '';
    this.composerAnhangArt = 'KEIN_ANHANG';
    this.composerAnzeigeName = '';
    this.composerUrl = '';
    this.composerUrlError = null;
    this.composerPdf = null;
    this.composerPdfError = null;
    this.composerPdfExistsOnServer = false;
    this.composerImageDescription = '';
    this.composerExistingBild = null;
    if (this.composerImagePreview) {
      URL.revokeObjectURL(this.composerImagePreview);
    }
    this.composerImage = null;
    this.composerImagePreview = null;
    this.composerImageError = null;
    this.publishError = null;
  }

  openDeleteConfirm(post: TogetherPost): void {
    this.postToDelete = post;
    this.modal.open(DELETE_MODAL);
  }

  confirmDelete(): void {
    if (!this.postToDelete) return;
    const id = this.postToDelete.id;
    this.modal.close(DELETE_MODAL);
    this.service
      .deletePost(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          const url = this.imageUrls.get(id);
          if (url) {
            URL.revokeObjectURL(url);
            this.imageUrls.delete(id);
          }
          this.posts = this.posts.filter(p => p.id !== id);
          this.postToDelete = null;
          this.cdr.markForCheck();
        },
        error: (err: {status?: number}) => {
          this.postToDelete = null;
          if (err?.status === 404) {
            this.deleteError = 'together.error.postNotFound';
            this.loadPosts();
          } else {
            this.deleteError = 'together.error.network';
          }
          this.cdr.markForCheck();
        },
      });
  }

  cancelDelete(): void {
    this.postToDelete = null;
    this.deleteError = null;
    this.modal.close(DELETE_MODAL);
  }

  downloadPdf(post: TogetherPost, event: Event): void {
    event.preventDefault();
    this.downloadError = null;
    this.service.downloadPdf(post.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: () => {
          this.downloadError = 'together.error.network';
          this.cdr.markForCheck();
        },
      });
  }

  openLightbox(post: TogetherPost, trigger: HTMLElement): void {
    this.lightboxTitel = post.titel;
    this.lightboxBeschreibung = post.bild?.beschreibung ?? null;
    this.lightboxUrl = this.imageUrls.get(post.id) ?? null;
    this.lightboxTrigger = trigger;
    this.lightboxOpen = true;
  }

  closeLightbox(): void {
    this.lightboxOpen = false;
    this.lightboxTrigger?.focus();
    this.lightboxTrigger = null;
  }

  formatDate(isoDate: string): string {
    return this.datePipe.transform(isoDate, 'mediumDate', undefined, this.locale as string) ?? isoDate;
  }

  isRight(index: number): boolean {
    return index % 2 === 0;
  }

  ngOnDestroy(): void {
    for (const url of this.imageUrls.values()) {
      URL.revokeObjectURL(url);
    }
    if (this.composerImagePreview) {
      URL.revokeObjectURL(this.composerImagePreview);
    }
  }

  private todayIso(): string {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private mapPublishError(err: {status?: number}): string {
    if (err?.status === 403) return 'together.error.forbidden';
    if (err?.status === 400) return 'together.error.invalid';
    return 'together.error.network';
  }
}
